import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDragLayer } from 'react-dnd';
import { useTranslation } from 'react-i18next';

import { useDialogs } from '@work4all/components';

import { useDataProvider, usePermissions, useUser } from '@work4all/data';
import { usePermissionsRights } from '@work4all/data/lib/hooks/use-permissions/hooks/use-permissions-rights';

import { Offer } from '@work4all/models/lib/Classes/Offer.entity';
import { DataRequest } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { FileType } from '@work4all/models/lib/File';

import { typeNameToEntity } from '@work4all/utils';
import { canAddSalesOpportunities } from '@work4all/utils/lib/permissions';

import { useCanFilterEntitiesByPermission } from '../../../../../../../components/data-tables/offers-table/hooks/use-can-filter-entities-by-permission';
import {
  CloneConvertDialogCallbacks,
  useCloneConvertDialogCallbacks,
} from '../../../../../../../components/data-tables/offers-table/hooks/use-clone-convert-dialog';
import { ENTITY_BY_CONVERT_CONFIGS } from '../../../../../../../components/data-tables/offers-table/hooks/use-clone-convert-erp';
import { useConvertBzObjectMutation } from '../../../../../../../components/data-tables/offers-table/hooks/use-convert-bz-object-mutation';
import { useConvertBzObjectToSalesOpportunity } from '../../../../../../../components/data-tables/offers-table/hooks/use-convert-bz-object-to-sales-opportunity-mutation';
import {
  ErpDialogConfirm,
  OnConfirmOptions,
} from '../../../../../../../components/data-tables/offers-table/types';
import { bpCombined } from '../../../../../../mask-overlays/mask-overlay/views/erp/hooks/use-bz-shadow-object-api/use-shadow-bz-object-graphql';
import { FileContext } from '../../../FileContext';

export const useCardDnd = (
  targetEntityProp: Entities,
  convertibleEntities: Entities[] = []
) => {
  const fileContext = useContext(FileContext);
  const { t } = useTranslation();
  const dialogs = useDialogs();

  const user = useUser();
  const { mutate } = useConvertBzObjectMutation({
    targetEntity: targetEntityProp,
  });
  const { item: monitorItem } = useDragLayer((monitor) => ({
    item: monitor.getItem(),
  }));

  const sourceEntity = useMemo(() => {
    return typeNameToEntity(monitorItem?.__typename);
  }, [monitorItem]);

  const requestData = useMemo<DataRequest>(() => {
    return {
      filter: [{ id: { $eq: monitorItem?.id } }],
      entity: sourceEntity,
      data: { id: null, businessPartnerContactCombined: bpCombined },
    };
  }, [sourceEntity, monitorItem]);

  const response = useDataProvider(requestData, !sourceEntity);

  const [item, setItem] = useState(monitorItem);
  useEffect(() => {
    if (response.data.length) {
      setItem({ ...monitorItem, ...response.data[0] });
    }
  }, [monitorItem, response.data]);

  const onConfirm = useCallback(
    async (confirm: ErpDialogConfirm[], options: OnConfirmOptions) => {
      const promises = confirm.map((singleConfirm) =>
        mutate({
          sourceEntity,
          sourceIds: [item.id],
          targetEntity: options.targetEntity,
          ...singleConfirm,
        })
      );
      const response = await Promise.all(promises);

      const key =
        sourceEntity === Entities.contract &&
        targetEntityProp === Entities.order
          ? 'COMMON.ERP.CONVERT.SUCCESS_CONTRACT'
          : 'COMMON.ERP.CONVERT.SUCCESS';
      if (response.flatMap((x) => x.errors).length === 0)
        await dialogs.alert({
          title: t('COMMON.INFO'),
          description: confirm.length > 1 ? t(key + '_MULTI') : t(key),
          closeLabel: t('COMMON.OK'),
        });
      else {
        await dialogs.alert({
          title: t('MASK.WARNING'),
          description: t('ERROR.COULD_NOT_REACH_SERVICE'),
          closeLabel: t('ALERTS.CLOSE'),
        });
      }
    },
    [mutate, sourceEntity, item?.id, dialogs, t]
  );

  const dialogProps: Omit<CloneConvertDialogCallbacks, 'open'> = useMemo(() => {
    return {
      onConfirm,
      sourceEntity,
      targetEntity: targetEntityProp,
      data: [item],
    };
  }, [item, sourceEntity, onConfirm, targetEntityProp]);

  const { open } = useCloneConvertDialogCallbacks(dialogProps);
  const mutateToSales = useConvertBzObjectToSalesOpportunity();

  const { isErpLightUser } = usePermissionsRights();

  const { canConvert } = usePermissions();
  const canUserConvertRecord = canConvert({ entity: targetEntityProp });
  const { filterEntitiesByPermission } = useCanFilterEntitiesByPermission();

  const value = useMemo(() => {
    const canDrag = () => {
      const canUserConvertRecord =
        convertibleEntities.includes(targetEntityProp);

      /**
       * If the user has the right to convert & the entity was defined
       * inside `ENTITY_BY_CONVERT_CONFIGS` as a key, that means you
       * can convert from it to other entities so you can drag an item
       * from the entity list.
       */
      return (
        canUserConvertRecord && !!ENTITY_BY_CONVERT_CONFIGS[targetEntityProp]
      );
    };
    const canDrop = () => {
      // Full context: https://work4all.atlassian.net/browse/WW-6286
      if (isErpLightUser)
        return (
          sourceEntity === Entities.contract &&
          targetEntityProp === Entities.deliveryNote
        );

      if (targetEntityProp === sourceEntity) return canUserConvertRecord;

      const sourceEntityConfigs = ENTITY_BY_CONVERT_CONFIGS[sourceEntity];
      if (
        targetEntityProp === Entities.salesOpportunities &&
        !canAddSalesOpportunities(user)
      )
        return false;
      return (
        sourceEntityConfigs &&
        sourceEntityConfigs.allowedEntities
          .filter(filterEntitiesByPermission)
          .includes(targetEntityProp)
      );
    };
    const canSkip = () => {
      if (targetEntityProp === sourceEntity) return false;

      const sourceEntityConfigs = ENTITY_BY_CONVERT_CONFIGS[sourceEntity];
      const allowedEntities = sourceEntityConfigs?.allowedEntities ?? [];
      const blockedEntities = sourceEntityConfigs?.blockedEntities ?? [];

      return ![...allowedEntities, ...blockedEntities].includes(
        targetEntityProp
      );
    };

    const isEntitiesIdentical = sourceEntity === targetEntityProp;

    const onDrop = async (item: object, targetEntity?: Entities) => {
      if (!('id' in item && typeof item.id === 'number'))
        throw new Error('Wrong dropped item in useCardDnd');

      if ((targetEntity ?? targetEntityProp) === Entities.salesOpportunities) {
        mutateToSales(sourceEntity, [item] as Offer[]);
      } else {
        open(
          targetEntity
            ? {
                targetEntity,
              }
            : undefined
        );
      }
    };

    return {
      canDrag,
      canDrop,
      canSkip,
      onDrop,
      isEntitiesIdentical,
      customerToSupplierConversionEntity:
        sourceEntity === Entities.contract &&
        isEntitiesIdentical &&
        fileContext?.type !== FileType.PROJECT
          ? Entities.order
          : undefined,
    };
  }, [
    sourceEntity,
    targetEntityProp,
    fileContext?.type,
    convertibleEntities,
    isErpLightUser,
    canUserConvertRecord,
    user,
    filterEntitiesByPermission,
    mutateToSales,
    open,
  ]);

  return value;
};
